// Performance monitoring and analytics utilities

interface PerformanceMetrics {
  name: string
  value: number
  timestamp: number
  url?: string
  userAgent?: string
}

class PerformanceMonitor {
  private metrics: PerformanceMetrics[] = []
  private isEnabled: boolean = true

  constructor() {
    this.initializeObservers()
  }

  private initializeObservers() {
    // Web Vitals observer
    if ('PerformanceObserver' in window) {
      // Largest Contentful Paint (LCP)
      const lcpObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries()
        const lastEntry = entries[entries.length - 1]
        this.recordMetric('LCP', lastEntry.startTime)
      })
      lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] })

      // First Input Delay (FID)
      const fidObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries()
        entries.forEach((entry: any) => {
          this.recordMetric('FID', entry.processingStart - entry.startTime)
        })
      })
      fidObserver.observe({ entryTypes: ['first-input'] })

      // Cumulative Layout Shift (CLS)
      let clsValue = 0
      const clsObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries()
        entries.forEach((entry: any) => {
          if (!entry.hadRecentInput) {
            clsValue += entry.value
          }
        })
        this.recordMetric('CLS', clsValue)
      })
      clsObserver.observe({ entryTypes: ['layout-shift'] })

      // Navigation timing
      window.addEventListener('load', () => {
        setTimeout(() => {
          const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming
          
          this.recordMetric('TTFB', navigation.responseStart - navigation.requestStart)
          this.recordMetric('DOM_LOAD', navigation.domContentLoadedEventEnd - navigation.fetchStart)
          this.recordMetric('FULL_LOAD', navigation.loadEventEnd - navigation.fetchStart)
        }, 0)
      })
    }
  }

  recordMetric(name: string, value: number, additionalData?: Record<string, any>) {
    if (!this.isEnabled) return

    const metric: PerformanceMetrics = {
      name,
      value,
      timestamp: Date.now(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      ...additionalData
    }

    this.metrics.push(metric)
    
    // Log to console in development
    if (import.meta.env.DEV) {
      console.log(`Performance Metric - ${name}:`, value, 'ms')
    }

    // Send to analytics in production
    if (import.meta.env.PROD) {
      this.sendToAnalytics(metric)
    }
  }

  private async sendToAnalytics(metric: PerformanceMetrics) {
    try {
      // In a real application, you would send this to your analytics service
      // For example: Google Analytics, Mixpanel, or custom analytics endpoint
      
      // Example implementation:
      // await fetch('/api/analytics/performance', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(metric)
      // })
      
      console.log('Would send to analytics:', metric)
    } catch (error) {
      console.error('Failed to send performance metric:', error)
    }
  }

  // Measure custom operations
  measureOperation<T>(name: string, operation: () => T | Promise<T>): T | Promise<T> {
    const startTime = performance.now()
    
    const result = operation()
    
    if (result instanceof Promise) {
      return result.finally(() => {
        const endTime = performance.now()
        this.recordMetric(`CUSTOM_${name}`, endTime - startTime)
      })
    } else {
      const endTime = performance.now()
      this.recordMetric(`CUSTOM_${name}`, endTime - startTime)
      return result
    }
  }

  // Get performance summary
  getMetricsSummary() {
    const summary: Record<string, { count: number; average: number; min: number; max: number }> = {}
    
    this.metrics.forEach(metric => {
      if (!summary[metric.name]) {
        summary[metric.name] = { count: 0, average: 0, min: Infinity, max: -Infinity }
      }
      
      const s = summary[metric.name]
      s.count++
      s.min = Math.min(s.min, metric.value)
      s.max = Math.max(s.max, metric.value)
      s.average = (s.average * (s.count - 1) + metric.value) / s.count
    })
    
    return summary
  }

  // Enable/disable monitoring
  setEnabled(enabled: boolean) {
    this.isEnabled = enabled
  }

  // Clear stored metrics
  clearMetrics() {
    this.metrics = []
  }
}

// Create singleton instance
export const performanceMonitor = new PerformanceMonitor()

// Utility functions for common measurements
export function measureApiCall<T>(name: string, apiCall: () => Promise<T>): Promise<T> {
  return performanceMonitor.measureOperation(`API_${name}`, apiCall) as Promise<T>
}

export function measureRender<T>(componentName: string, renderFn: () => T): T {
  return performanceMonitor.measureOperation(`RENDER_${componentName}`, renderFn) as T
}

// Resource loading performance
export function trackResourceLoading() {
  window.addEventListener('load', () => {
    const resources = performance.getEntriesByType('resource') as PerformanceResourceTiming[]
    
    resources.forEach(resource => {
      const loadTime = resource.responseEnd - resource.startTime
      const resourceType = resource.initiatorType || 'unknown'
      
      performanceMonitor.recordMetric(
        `RESOURCE_${resourceType.toUpperCase()}`,
        loadTime,
        { url: resource.name }
      )
    })
  })
}

// Memory usage tracking (if available)
export function trackMemoryUsage() {
  if ('memory' in performance) {
    const memory = (performance as any).memory
    
    performanceMonitor.recordMetric('MEMORY_USED', memory.usedJSHeapSize)
    performanceMonitor.recordMetric('MEMORY_TOTAL', memory.totalJSHeapSize)
    performanceMonitor.recordMetric('MEMORY_LIMIT', memory.jsHeapSizeLimit)
  }
}

// Initialize performance tracking
export function initializePerformanceTracking() {
  trackResourceLoading()
  
  // Track memory usage periodically
  setInterval(trackMemoryUsage, 30000) // Every 30 seconds
  
  // Track route changes
  if (window.history && window.history.pushState) {
    const originalPushState = window.history.pushState
    window.history.pushState = function(...args) {
      performanceMonitor.recordMetric('ROUTE_CHANGE', performance.now())
      return originalPushState.apply(window.history, args)
    }
  }
}